<script setup>
import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CurrencyInput from 'dashboard/components-next/input/CurrencyInput.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import DateField from 'dashboard/components-next/date-field/DateField.vue';
import TalkviaInventoryProductsAPI from 'dashboard/api/talkviaInventoryProducts';
import TalkviaInventoryProductGroupsAPI from 'dashboard/api/talkviaInventoryProductGroups';
import {
  ContractsAPI,
  ContractTemplatesAPI,
} from 'dashboard/api/talkviaContracts';
import QuotesAPI from 'dashboard/api/talkviaQuotes';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { formatCep, onlyDigits } from 'shared/helpers/talkviaInputMasks';
import ContractSendDialog from '../contracts/ContractSendDialog.vue';
import { contractScheduleError } from '../contracts/contractWizard';
import {
  normalizeRentalDays,
  rentalItemTotal,
  itemRentalDays,
  suggestRentalDays,
} from '../contracts/rentalDays';
import {
  buildCustomQuoteDocument,
  buildDefaultQuoteDocument,
  quoteDocumentTitle,
  renderQuoteTemplateBody,
} from '../contracts/quoteDocument';
import { quoteTemplateValues } from '../contracts/quoteTemplateValues';
import { renderQuoteAttachment } from '../contracts/quoteAttachment';

// Orçamento rápido na Central do cliente: monta itens do Estoque e envia pelo mesmo
// fluxo de orçamentos dos Contratos (ContractSendDialog + `send_quote`), sem criar contrato.
const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  // Código visível da conversa (display_id).
  conversationId: { type: [Number, String], default: null },
  inboxId: { type: [Number, String], default: null },
});

const emit = defineEmits(['sent', 'saved']);

const CUSTOM_SERVICE_VALUE = 'custom_service';
const CUSTOM_SERVICE_TYPE = 'CustomService';
const MAX_NOTES_LENGTH = 1000;
const MAX_QUANTITY_DIGITS = 5;

const copy = {
  title: 'Orçamento rápido',
  description:
    'Monte o orçamento com os itens do estoque e envie nesta conversa.',
  addItem: 'Adicionar item',
  addItemPlaceholder: 'Buscar produto, grupo ou serviço',
  searchPlaceholder: 'Digite o nome do item',
  noResults: 'Nenhum item encontrado.',
  customService: 'Serviço personalizado',
  customServiceName: 'Descrição do serviço',
  customServicePlaceholder: 'Ex.: Montagem e desmontagem',
  productPrefix: 'Estoque',
  groupPrefix: 'Grupo',
  items: 'Itens',
  quantity: 'Quantidade',
  unitPrice: 'Valor unitário',
  subtotal: 'Subtotal',
  remove: 'Remover',
  total: 'Total:',
  noItems: 'Adicione ao menos um item para enviar o orçamento.',
  available: 'Disponível:',
  availableOn: 'Disponível na data da entrega:',
  availableBetween: (from, until) => `Disponível de ${from} a ${until}:`,
  loadingStock: 'Consultando estoque…',
  stockWarning:
    'Quantidade acima do disponível. O orçamento pode ser enviado, mas confira o estoque antes de confirmar com o cliente.',
  schedule: 'Entrega e retirada (opcional)',
  deliveryAt: 'Entrega',
  pickupAt: 'Retirada',
  rentalDays: 'Diárias',
  rentalDaysHint: 'Multiplica o valor de cada item.',
  rentalDaysSuggested: 'Calculado pelas datas; ajuste se precisar.',
  rentalDaysSuffix: days => `× ${days} diárias`,
  chargePerDay: 'Cobrar por diária',
  fixedValue: 'Valor fechado, sem diárias',
  notes: 'Observações (opcional)',
  notesPlaceholder: 'Condições, prazos ou detalhes combinados com o cliente.',
  loadingCatalog: 'Carregando itens do estoque…',
  loadFailed:
    'Não foi possível carregar os itens do estoque. Tente novamente em instantes.',
  retry: 'Tentar novamente',
  emptyTitle: 'Nenhum produto ativo no estoque',
  emptyDescription:
    'Cadastre produtos ou grupos no Estoque para montar orçamentos rápidos. Você ainda pode incluir um serviço personalizado.',
  openInventory: 'Abrir Estoque',
  cancel: 'Cancelar',
  send: 'Enviar orçamento',
  sending: 'Carregando…',
  invalidQuantity: 'Informe uma quantidade de pelo menos 1.',
  invalidName: 'Informe a descrição do serviço.',
  sent: 'Orçamento enviado na conversa.',
  receiptSent: 'Recibo enviado na conversa.',
  receiptGenerated: 'Recibo gerado para download.',
  sendFailed:
    'Não foi possível enviar. Confira a caixa e os dados informados e tente novamente.',
  receiptFailed:
    'Não foi possível gerar o recibo. Confira os dados do pagamento e tente novamente.',
  pdfFailed: 'Não foi possível iniciar a impressão do PDF.',
};

const dialogRef = ref(null);
const sendDialogRef = ref(null);
const products = ref([]);
const groups = ref([]);
const isLoadingCatalog = ref(false);
const hasCatalogError = ref(false);
const selectedOption = ref('');
const lines = ref([]);
// Disponibilidade do período (entrega até a véspera da retirada), de todos os itens de uma vez.
const stock = reactive({
  key: '',
  loading: false,
  failed: false,
  from: '',
  until: '',
  products: {},
  groups: {},
});
let stockRequestId = 0;
const isSending = ref(false);
const editingQuote = ref(null);
const quoteAddress = reactive({});
const isLookingUpCep = ref(false);
let cepRequest = 0;
let lastResolvedCep = '';

const lookupQuoteCep = async () => {
  const cep = onlyDigits(quoteAddress.zip_code, 8);
  if (cep.length !== 8 || isLookingUpCep.value || cep === lastResolvedCep)
    return;
  cepRequest += 1;
  const request = cepRequest;
  isLookingUpCep.value = true;
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();
    if (request !== cepRequest || onlyDigits(quoteAddress.zip_code, 8) !== cep)
      return;
    if (!response.ok || data.erro) throw new Error('CEP não encontrado');
    Object.assign(quoteAddress, {
      address: data.logradouro || quoteAddress.address,
      neighborhood: data.bairro || quoteAddress.neighborhood,
      city: data.localidade || quoteAddress.city,
      state: data.uf || quoteAddress.state,
    });
    lastResolvedCep = cep;
  } catch {
    if (request === cepRequest)
      useAlert(
        'Não foi possível consultar o CEP. Confira o número e tente novamente.'
      );
  } finally {
    if (request === cepRequest) isLookingUpCep.value = false;
  }
};

const updateQuoteCep = value => {
  const formatted = formatCep(value);
  if (formatted === quoteAddress.zip_code) return;
  cepRequest += 1;
  isLookingUpCep.value = false;
  lastResolvedCep = '';
  quoteAddress.zip_code = formatted;
  lookupQuoteCep();
};

const maskQuoteCep = event => {
  event.target.value = formatCep(event.target.value);
};
onBeforeUnmount(() => {
  cepRequest += 1;
});

const form = reactive({
  scheduledAt: '',
  pickupAt: '',
  notes: '',
  rentalDays: '1',
  validityHours: '24',
});
// Enquanto o atendente não digitar as diárias, elas seguem as datas de entrega e retirada.
const rentalDaysTouched = ref(false);
const rentalDays = computed(() => normalizeRentalDays(form.rentalDays));
const rentalDaysFromDates = computed(() =>
  suggestRentalDays(form.scheduledAt, form.pickupAt)
);
const syncRentalDaysWithDates = () => {
  if (rentalDaysTouched.value || !rentalDaysFromDates.value) return;
  form.rentalDays = String(rentalDaysFromDates.value);
};
const updateRentalDays = value => {
  rentalDaysTouched.value = true;
  form.rentalDays = onlyDigits(value, 3);
};
let lineSequence = 0;
let catalogRequestId = 0;

const { currentAccount, accountId } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const canOpenInventory = computed(() => {
  const isEnabled = isFeatureEnabledonAccount.value;
  if (typeof isEnabled !== 'function') return false;
  return (
    isEnabled(accountId.value, 'talkvia_logistics_inventory_routes') ||
    isEnabled(accountId.value, 'talkvia_restaurant')
  );
});
const inventoryRoute = computed(() => ({
  name: 'talkvia_inventory',
  params: { accountId: accountId.value },
}));

const currency = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value || 0)
  );
const formatQuantity = value =>
  Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });

const isActive = item => item?.active !== false;

const catalogOptions = computed(() => [
  {
    value: CUSTOM_SERVICE_VALUE,
    label: copy.customService,
    icon: 'i-hugeicons-wrench-01',
  },
  ...products.value.filter(isActive).map(product => ({
    value: `TalkviaInventoryProduct:${product.id}`,
    label: `${copy.productPrefix} · ${product.name}`,
    icon: 'i-hugeicons-package',
  })),
  ...groups.value.filter(isActive).map(group => ({
    value: `TalkviaInventoryProductGroup:${group.id}`,
    label: `${copy.groupPrefix} · ${group.name}`,
    icon: 'i-hugeicons-boxes',
  })),
]);

const hasInventoryItems = computed(
  () => products.value.some(isActive) || groups.value.some(isActive)
);

const deliveryDate = computed(() =>
  String(form.scheduledAt || '').slice(0, 10)
);
const pickupDate = computed(() => String(form.pickupAt || '').slice(0, 10));

const INVENTORY_TYPES = {
  TalkviaInventoryProduct: 'products',
  TalkviaInventoryProductGroup: 'groups',
};
const inventoryLines = () =>
  lines.value.filter(line => INVENTORY_TYPES[line.productType]);
const stockRequestKey = () => {
  const ids = inventoryLines()
    .map(line => `${line.productType}:${line.productId}`)
    .sort()
    .join(',');
  return `${deliveryDate.value}|${pickupDate.value}|${ids}`;
};

const shortDate = value => {
  const [year, month, day] = String(value).split('-');
  return year && month && day ? `${day}/${month}` : value;
};

// Com entrega informada: menor saldo livre entre a entrega e a véspera da retirada
// (descontando contratos do período); sem entrega: o saldo atual.
const availabilityFor = line => {
  const bucket = INVENTORY_TYPES[line.productType];
  if (!bucket) return { loading: false, value: null, byDate: false };
  if (!deliveryDate.value)
    return { loading: false, value: line.onHand, byDate: false };
  const value = stock[bucket][line.productId];
  if (stock.loading || (!stock.failed && value === undefined))
    return { loading: true, value: null, byDate: false };
  if (stock.failed || value === undefined)
    return { loading: false, value: line.onHand, byDate: false };
  return { loading: false, value, byDate: true };
};

const availabilityLabel = line => {
  const { byDate } = availabilityFor(line);
  if (!byDate) return copy.available;
  if (stock.until && stock.until !== stock.from)
    return copy.availableBetween(shortDate(stock.from), shortDate(stock.until));
  return copy.availableOn;
};

const exceedsStock = line => {
  const { value } = availabilityFor(line);
  return value !== null && value !== undefined && line.quantity > value;
};

const lineTotal = line =>
  rentalItemTotal(
    line.quantity,
    line.unitPrice,
    itemRentalDays(line, rentalDays.value)
  );
const total = computed(() =>
  lines.value.reduce((sum, line) => sum + lineTotal(line), 0)
);

const lineError = line => {
  if (!(Number(line.quantity) >= 1)) return copy.invalidQuantity;
  if (!String(line.name || '').trim()) return copy.invalidName;
  return '';
};

const scheduleError = computed(() =>
  contractScheduleError({
    scheduled_at: form.scheduledAt,
    pickup_at: form.pickupAt,
  })
);

const canSend = computed(
  () =>
    !isSending.value &&
    !!props.contact?.id &&
    lines.value.length > 0 &&
    lines.value.every(line => !lineError(line)) &&
    !scheduleError.value &&
    form.notes.length <= MAX_NOTES_LENGTH
);

const refreshStock = async () => {
  const key = stockRequestKey();
  if (!deliveryDate.value || !inventoryLines().length) {
    stockRequestId += 1;
    Object.assign(stock, { key, loading: false, failed: false });
    return;
  }
  if (key === stock.key && !stock.failed) return;
  stockRequestId += 1;
  const requestId = stockRequestId;
  Object.assign(stock, { key, loading: true, failed: false });
  const idsOf = type =>
    inventoryLines()
      .filter(line => line.productType === type)
      .map(line => line.productId);
  try {
    const { data } = await TalkviaInventoryProductsAPI.availability({
      from: deliveryDate.value,
      to: pickupDate.value,
      productIds: idsOf('TalkviaInventoryProduct'),
      groupIds: idsOf('TalkviaInventoryProductGroup'),
    });
    if (requestId !== stockRequestId) return;
    Object.assign(stock, {
      loading: false,
      from: data?.from || deliveryDate.value,
      until: data?.until || deliveryDate.value,
      products: data?.products || {},
      groups: data?.groups || {},
    });
  } catch {
    if (requestId !== stockRequestId) return;
    Object.assign(stock, { loading: false, failed: true });
  }
};

const loadCatalog = async () => {
  catalogRequestId += 1;
  const requestId = catalogRequestId;
  isLoadingCatalog.value = true;
  hasCatalogError.value = false;
  try {
    const [productsResponse, groupsResponse] = await Promise.all([
      TalkviaInventoryProductsAPI.get({ active: true }),
      TalkviaInventoryProductGroupsAPI.get({ active: true }),
    ]);
    if (requestId !== catalogRequestId) return;
    products.value = productsResponse.data?.products || [];
    groups.value = Array.isArray(groupsResponse.data)
      ? groupsResponse.data
      : [];
  } catch {
    if (requestId !== catalogRequestId) return;
    hasCatalogError.value = true;
  } finally {
    if (requestId === catalogRequestId) isLoadingCatalog.value = false;
  }
};

const findCatalogItem = value => {
  const [type, id] = String(value).split(':');
  const source =
    type === 'TalkviaInventoryProduct' ? products.value : groups.value;
  const item = source.find(entry => String(entry.id) === id);
  return item ? { type, item } : null;
};

const addLine = value => {
  selectedOption.value = '';
  if (!value) return;
  lineSequence += 1;
  if (value === CUSTOM_SERVICE_VALUE) {
    lines.value = [
      ...lines.value,
      {
        key: lineSequence,
        productType: CUSTOM_SERVICE_TYPE,
        productId: null,
        name: '',
        unit: '',
        onHand: null,
        quantity: 1,
        unitPrice: '',
        skipRentalDays: false,
      },
    ];
    return;
  }

  const found = findCatalogItem(value);
  if (!found) return;
  const existing = lines.value.find(
    line =>
      line.productType === found.type &&
      String(line.productId) === String(found.item.id)
  );
  if (existing) {
    existing.quantity += 1;
    return;
  }

  const line = {
    key: lineSequence,
    productType: found.type,
    productId: found.item.id,
    name: found.item.name,
    unit: found.item.unit || '',
    onHand:
      found.item.quantity_on_hand === undefined ||
      found.item.quantity_on_hand === null
        ? null
        : Number(found.item.quantity_on_hand),
    quantity: 1,
    unitPrice: Number(found.item.sale_price || 0).toFixed(2),
    skipRentalDays: false,
  };
  lines.value = [...lines.value, line];
  refreshStock();
};

const removeLine = key => {
  lines.value = lines.value.filter(line => line.key !== key);
};

const updateQuantity = (line, value) => {
  const digits = onlyDigits(value, MAX_QUANTITY_DIGITS);
  line.quantity = digits ? Number(digits) : 0;
};

const refreshQuantityMask = event => {
  const digits = onlyDigits(event.target.value, MAX_QUANTITY_DIGITS);
  if (event.target.value !== digits) event.target.value = digits;
};

const updateDelivery = value => {
  form.scheduledAt = value || '';
  syncRentalDaysWithDates();
  refreshStock();
};

const updatePickup = value => {
  form.pickupAt = value || '';
  syncRentalDaysWithDates();
  refreshStock();
};

const resetForm = () => {
  cepRequest += 1;
  isLookingUpCep.value = false;
  lastResolvedCep = '';
  lines.value = [];
  selectedOption.value = '';
  form.scheduledAt = '';
  form.pickupAt = '';
  form.notes = '';
  form.rentalDays = '1';
  form.validityHours = '24';
  editingQuote.value = null;
  rentalDaysTouched.value = false;
  stockRequestId += 1;
  Object.assign(stock, {
    key: '',
    loading: false,
    failed: false,
    from: '',
    until: '',
    products: {},
    groups: {},
  });
};

const contactAttribute = keys => {
  const source = {
    ...(props.contact?.additional_attributes || {}),
    ...(props.contact?.custom_attributes || {}),
  };
  return keys.map(key => source[key]).find(value => value) || '';
};

const recipientDocument = () => {
  const cpf = contactAttribute(['cpf', 'CPF', 'document', 'documento']);
  const cnpj = contactAttribute(['cnpj', 'CNPJ']);
  const isCompany = contactAttribute(['tipo_pessoa', 'tipoPessoa']) === 'pj';
  if (isCompany && cnpj) return { value: cnpj, type: 'cnpj' };
  if (cpf) return { value: cpf, type: 'cpf' };
  return cnpj ? { value: cnpj, type: 'cnpj' } : { value: '', type: '' };
};

const recipientName = documentType => {
  if (documentType !== 'cnpj') return props.contact?.name || '';
  return (
    contactAttribute([
      'razao_social',
      'razaoSocial',
      'nome_fantasia',
      'nomeFantasia',
    ]) ||
    props.contact?.name ||
    ''
  );
};

const recipientAddress = () => ({
  zip_code: contactAttribute([
    'cep',
    'zip_code',
    'zipCode',
    'zipcode',
    'postal_code',
  ]),
  address: contactAttribute([
    'endereco',
    'address',
    'addressLine',
    'address_line',
    'street',
    'logradouro',
  ]),
  number: contactAttribute([
    'numero',
    'number',
    'addressNumber',
    'address_number',
  ]),
  complement: contactAttribute([
    'complemento',
    'complement',
    'addressComplement',
    'address_complement',
  ]),
  neighborhood: contactAttribute(['bairro', 'neighborhood', 'district']),
  city: contactAttribute(['cidade', 'city']),
  state: contactAttribute(['uf', 'state', 'province']),
});

const buildPayload = () => {
  const document = recipientDocument();
  const expiry =
    editingQuote.value &&
    Number(form.validityHours) === editingQuote.value.validity_hours
      ? editingQuote.value.expires_at
      : new Date(
          new Date(editingQuote.value?.created_at || Date.now()).getTime() +
            Number(form.validityHours) * 3600000
        ).toISOString();
  return {
    ...(editingQuote.value?.payload || {}),
    contact_id: props.contact.id,
    recipient_name:
      editingQuote.value?.payload.recipient_name ??
      recipientName(document.type),
    recipient_cpf: editingQuote.value?.payload.recipient_cpf ?? document.value,
    recipient_phone:
      editingQuote.value?.payload.recipient_phone ??
      props.contact?.phone_number ??
      '',
    scheduled_at: form.scheduledAt,
    pickup_at: form.pickupAt,
    rental_days: rentalDays.value,
    notes: form.notes.trim(),
    recipient_address: { ...quoteAddress },
    validity_hours: Number(form.validityHours),
    expires_at: expiry,
    ...(editingQuote.value
      ? {
          quote_id: editingQuote.value.id,
        }
      : {}),
    invoice_items: lines.value.map(line => ({
      product_type: line.productType,
      product_id: line.productId,
      product_name: String(line.name).trim(),
      quantity: Number(line.quantity),
      unit_price: Number(line.unitPrice || 0),
      total: lineTotal(line),
      ...(line.skipRentalDays ? { skip_rental_days: true } : {}),
    })),
  };
};

// Modelos de orçamento ativos (Contratos → Modelos), os mesmos do envio pelos Contratos.
const quoteTemplates = ref([]);
const loadQuoteTemplates = async () => {
  try {
    const { data } = await ContractTemplatesAPI.get();
    quoteTemplates.value = (data || []).filter(
      template => template.metadata?.kind === 'quote' && template.active
    );
  } catch {
    quoteTemplates.value = [];
  }
};

const open = (quote = null) => {
  if (!props.contact?.id) return;
  loadQuoteTemplates();
  resetForm();
  Object.assign(quoteAddress, recipientAddress());
  if (quote) {
    editingQuote.value = quote;
    const payload = quote.payload;
    form.scheduledAt = payload.scheduled_at || '';
    form.pickupAt = payload.pickup_at || '';
    form.notes = payload.notes || '';
    form.rentalDays = String(payload.rental_days || 1);
    form.validityHours = String(quote.validity_hours);
    rentalDaysTouched.value = true;
    Object.assign(quoteAddress, payload.recipient_address || {});
    lines.value = (payload.invoice_items || []).map((item, index) => ({
      key: index + 1,
      productType: item.product_type,
      productId: item.product_id,
      name: item.product_name,
      quantity: item.quantity,
      unitPrice: String(item.unit_price),
      skipRentalDays: !!item.skip_rental_days,
      unit: '',
      onHand: null,
    }));
    lineSequence = lines.value.length;
  }
  quoteAddress.zip_code = formatCep(quoteAddress.zip_code);
  dialogRef.value?.open();
  if (!products.value.length && !groups.value.length) loadCatalog();
  else {
    // Reabrir consulta de novo: contratos podem ter mudado desde a última vez.
    stock.key = '';
    refreshStock();
  }
};

const close = () => {
  if (isSending.value) return;
  dialogRef.value?.close();
};

// Revisão, destino e formato (mensagem, PDF ou PNG) ficam no diálogo padrão de orçamentos.
const validValidity = computed(
  () =>
    Number.isInteger(Number(form.validityHours)) &&
    Number(form.validityHours) >= 1 &&
    Number(form.validityHours) <= 8760
);

const reviewAndSend = () => {
  if (!canSend.value || !validValidity.value) return;
  sendDialogRef.value?.openQuote(
    buildPayload(),
    props.inboxId ? { inboxId: props.inboxId } : {}
  );
};

// Na caixa desta conversa, o envio vai exatamente para ela (display_id validado no servidor).
const withConversation = payload =>
  props.conversationId && Number(payload?.inbox_id) === Number(props.inboxId)
    ? { ...payload, conversation_id: props.conversationId }
    : payload;

const saveQuote = async () => {
  if (!canSend.value || !validValidity.value || isSending.value) return;
  isSending.value = true;
  try {
    const payload = buildPayload();
    const { data } = editingQuote.value
      ? await QuotesAPI.update(editingQuote.value.id, payload)
      : await QuotesAPI.create(payload);
    editingQuote.value = data;
    useAlert(
      'Orçamento salvo. Para enviar as alterações ao cliente, use Enviar orçamento.'
    );
    emit('saved', data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível salvar o orçamento.'
    );
  } finally {
    isSending.value = false;
  }
};

// Com modelo escolhido, o PDF/PNG usa o corpo dele, como no envio pelos Contratos.
const quoteHtml = payload => {
  const template = quoteTemplates.value.find(
    item => Number(item.id) === Number(payload.quote_template_id)
  );
  if (template?.body)
    return buildCustomQuoteDocument({
      payload,
      bodyHtml: renderQuoteTemplateBody(
        template.body,
        quoteTemplateValues(payload)
      ),
    });
  return buildDefaultQuoteDocument({ payload, account: currentAccount.value });
};

const withQuoteAttachment = async payload => {
  const format = payload.quote_attachment_format;
  if (!['pdf', 'png'].includes(format)) return payload;
  const quoteFiles = await renderQuoteAttachment({
    html: quoteHtml(payload),
    format,
    name: quoteDocumentTitle(payload),
  });
  return { ...payload, quote_files: quoteFiles };
};

const handleSendQuote = async payload => {
  if (isSending.value) return;
  isSending.value = true;
  const isReceipt = payload.document_kind === 'receipt';
  try {
    const request = withConversation(payload);
    if (isReceipt) await ContractsAPI.sendReceipt(request);
    else await ContractsAPI.sendQuote(await withQuoteAttachment(request));
    sendDialogRef.value?.close();
    dialogRef.value?.close();
    resetForm();
    useAlert(isReceipt ? copy.receiptSent : copy.sent);
    emit('sent', { documentKind: isReceipt ? 'receipt' : 'quote' });
  } catch (error) {
    useAlert(error?.response?.data?.error || copy.sendFailed);
  } finally {
    isSending.value = false;
  }
};

const handleGenerateQuotePdf = payload => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    useAlert(copy.pdfFailed);
    return;
  }
  printWindow.document.open();
  printWindow.document.write(quoteHtml(payload));
  printWindow.document.close();
  window.setTimeout(() => {
    try {
      printWindow.focus();
      printWindow.print();
    } catch {
      useAlert(copy.pdfFailed);
    }
  }, 250);
};

const downloadFile = file => {
  const bytes = Uint8Array.from(atob(file.data), character =>
    character.charCodeAt(0)
  );
  const url = URL.createObjectURL(
    new Blob([bytes], { type: file.content_type })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const handleGenerateReceipt = async payload => {
  if (isSending.value) return;
  isSending.value = true;
  try {
    const { data } = await ContractsAPI.generateReceipt(payload);
    (data?.files || []).forEach(downloadFile);
    useAlert(copy.receiptGenerated);
  } catch {
    useAlert(copy.receiptFailed);
  } finally {
    isSending.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialogRef"
    solid
    width="2xl"
    overflow-y-auto
    sticky-header-footer
    floating
    :title="copy.title"
    :description="copy.description"
  >
    <div class="grid gap-5">
      <p
        v-if="isLoadingCatalog"
        class="mb-0 flex items-center gap-2 text-sm text-n-slate-11"
        role="status"
      >
        <span
          class="i-hugeicons-loading-03 size-4 animate-spin"
          aria-hidden="true"
        />
        {{ copy.loadingCatalog }}
      </p>
      <div
        v-else-if="hasCatalogError"
        class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
        data-test="catalog-error"
      >
        <p class="mb-0 text-sm text-n-slate-11">{{ copy.loadFailed }}</p>
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-hugeicons-refresh"
          :label="copy.retry"
          type="button"
          @click="loadCatalog"
        />
      </div>
      <div
        v-else-if="!hasInventoryItems"
        class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
        data-test="empty-inventory"
      >
        <span
          class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-brand/10 text-n-brand"
        >
          <span class="i-hugeicons-package size-5" aria-hidden="true" />
        </span>
        <div class="min-w-0">
          <p class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ copy.emptyTitle }}
          </p>
          <p class="mb-0 mt-0.5 text-xs leading-relaxed text-n-slate-11">
            {{ copy.emptyDescription }}
          </p>
          <router-link
            v-if="canOpenInventory"
            :to="inventoryRoute"
            class="mt-2 inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
            data-test="open-inventory"
          >
            {{ copy.openInventory }}
            <span
              class="i-hugeicons-arrow-up-right-01 size-3.5"
              aria-hidden="true"
            />
          </router-link>
        </div>
      </div>

      <div v-if="!isLoadingCatalog && !hasCatalogError" class="grid gap-2">
        <label class="text-xs font-medium text-n-slate-11">
          {{ copy.addItem }}
        </label>
        <ComboBox
          :model-value="selectedOption"
          :options="catalogOptions"
          :placeholder="copy.addItemPlaceholder"
          :search-placeholder="copy.searchPlaceholder"
          :empty-state="copy.noResults"
          :disabled="isSending"
          teleport-to-body
          data-test="catalog-select"
          @update:model-value="addLine"
        />
      </div>

      <section class="grid gap-2" :aria-label="copy.items">
        <p
          v-if="!lines.length"
          class="mb-0 rounded-xl border border-dashed border-n-weak p-4 text-center text-sm text-n-slate-11"
        >
          {{ copy.noItems }}
        </p>
        <ul v-else class="m-0 grid list-none gap-2 p-0">
          <li
            v-for="line in lines"
            :key="line.key"
            class="grid gap-3 rounded-xl border border-n-weak p-3"
            data-test="quote-line"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <Input
                  v-if="line.productType === 'CustomService'"
                  v-model="line.name"
                  size="sm"
                  :label="copy.customServiceName"
                  :placeholder="copy.customServicePlaceholder"
                  :disabled="isSending"
                />
                <p
                  v-else
                  class="mb-0 truncate text-sm font-medium text-n-slate-12"
                  :title="line.name"
                >
                  {{ line.name }}
                </p>
                <p
                  v-if="availabilityFor(line).loading"
                  class="mb-0 mt-0.5 text-xs text-n-slate-11"
                  role="status"
                >
                  {{ copy.loadingStock }}
                </p>
                <p
                  v-else-if="availabilityFor(line).value !== null"
                  class="mb-0 mt-0.5 text-xs tabular-nums text-n-slate-11"
                  data-test="line-stock"
                >
                  {{ availabilityLabel(line) }}
                  {{ formatQuantity(availabilityFor(line).value) }}
                  {{ line.unit }}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                color="slate"
                icon="i-hugeicons-delete-02"
                :aria-label="`${copy.remove} ${line.name}`"
                :title="copy.remove"
                :disabled="isSending"
                type="button"
                data-test="remove-line"
                @click="removeLine(line.key)"
              />
            </div>
            <div
              class="grid gap-3 sm:grid-cols-[minmax(0,7rem)_minmax(0,1fr)_auto]"
            >
              <Input
                :model-value="line.quantity ? String(line.quantity) : ''"
                size="sm"
                inputmode="numeric"
                :label="copy.quantity"
                :disabled="isSending"
                :message="line.quantity >= 1 ? '' : copy.invalidQuantity"
                :message-type="line.quantity >= 1 ? 'info' : 'error'"
                data-test="line-quantity"
                @update:model-value="updateQuantity(line, $event)"
                @input="refreshQuantityMask"
              />
              <CurrencyInput
                v-model="line.unitPrice"
                size="sm"
                :label="copy.unitPrice"
                :disabled="isSending"
                data-test="line-price"
              />
              <div class="flex flex-col justify-end sm:items-end">
                <span class="text-xs text-n-slate-11">{{ copy.subtotal }}</span>
                <p
                  class="mb-0 py-1.5 text-sm font-semibold tabular-nums text-n-slate-12"
                  data-test="line-total"
                >
                  {{ currency(lineTotal(line)) }}
                </p>
                <span
                  v-if="rentalDays > 1"
                  class="text-xs text-n-slate-11"
                  data-test="line-rental-days"
                >
                  {{
                    line.skipRentalDays
                      ? copy.fixedValue
                      : copy.rentalDaysSuffix(rentalDays)
                  }}
                </span>
              </div>
            </div>
            <label
              v-if="rentalDays > 1"
              class="flex w-fit cursor-pointer items-center gap-2 text-xs text-n-slate-11"
            >
              <Checkbox
                :model-value="!line.skipRentalDays"
                :disabled="isSending"
                data-test="line-charge-per-day"
                @update:model-value="line.skipRentalDays = !$event"
              />
              {{ copy.chargePerDay }}
            </label>
            <p
              v-if="exceedsStock(line)"
              class="mb-0 flex items-start gap-2 rounded-lg bg-n-amber-3 px-3 py-2 text-xs text-n-amber-11"
              role="status"
              data-test="stock-warning"
            >
              <span
                class="i-hugeicons-alert-02 mt-0.5 size-3.5 shrink-0"
                aria-hidden="true"
              />
              {{ copy.stockWarning }}
            </p>
          </li>
        </ul>
      </section>

      <section class="grid gap-3">
        <h3 class="mb-0 text-xs font-semibold text-n-slate-11">
          {{ copy.schedule }}
        </h3>
        <div class="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_7rem]">
          <DateField
            :model-value="form.scheduledAt"
            with-time
            size="sm"
            :label="copy.deliveryAt"
            :disabled="isSending"
            data-test="delivery-at"
            @update:model-value="updateDelivery"
          />
          <DateField
            :model-value="form.pickupAt"
            with-time
            size="sm"
            :min="form.scheduledAt"
            :label="copy.pickupAt"
            :disabled="isSending"
            :message="scheduleError"
            :message-type="scheduleError ? 'error' : 'info'"
            data-test="pickup-at"
            @update:model-value="updatePickup"
          />
          <Input
            :model-value="form.rentalDays"
            size="sm"
            inputmode="numeric"
            :label="copy.rentalDays"
            :disabled="isSending"
            data-test="rental-days"
            @update:model-value="updateRentalDays"
          />
        </div>
        <p class="mb-0 text-xs text-n-slate-11" data-test="rental-days-hint">
          {{
            !rentalDaysTouched && rentalDaysFromDates
              ? copy.rentalDaysSuggested
              : copy.rentalDaysHint
          }}
        </p>
      </section>

      <section class="grid gap-3">
        <h3 class="mb-0 text-xs font-semibold text-n-slate-11">
          Endereço de entrega
        </h3>
        <div class="grid gap-3 sm:grid-cols-2">
          <Input
            :model-value="quoteAddress.zip_code"
            label="CEP"
            placeholder="00000-000"
            inputmode="numeric"
            maxlength="9"
            :message="isLookingUpCep ? 'Consultando CEP…' : ''"
            :disabled="isSending"
            data-test="quote-cep"
            @update:model-value="updateQuoteCep"
            @input="maskQuoteCep"
            @blur="lookupQuoteCep"
          />
          <Input
            v-model="quoteAddress.address"
            label="Endereço"
            :disabled="isSending"
          />
          <Input
            v-model="quoteAddress.number"
            label="Número"
            :disabled="isSending"
          />
          <Input
            v-model="quoteAddress.complement"
            label="Complemento"
            :disabled="isSending"
          />
          <Input
            v-model="quoteAddress.neighborhood"
            label="Bairro"
            :disabled="isSending"
          />
          <Input
            v-model="quoteAddress.city"
            label="Cidade"
            :disabled="isSending"
          />
          <Input
            v-model="quoteAddress.state"
            label="UF"
            :disabled="isSending"
          />
        </div>
      </section>
      <Input
        v-model="form.validityHours"
        label="Validade do orçamento (horas)"
        inputmode="numeric"
        :disabled="isSending"
        :message="
          validValidity
            ? 'Padrão: 24 horas. Excluído 24 horas após expirar.'
            : 'Informe de 1 a 8760 horas.'
        "
        :message-type="validValidity ? 'info' : 'error'"
        data-test="quote-validity"
      />
      <TextArea
        v-model="form.notes"
        :label="copy.notes"
        :placeholder="copy.notesPlaceholder"
        :max-length="MAX_NOTES_LENGTH"
        show-character-count
        auto-height
        :disabled="isSending"
      />
    </div>

    <template #footer>
      <div
        class="flex w-full flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center"
      >
        <p class="mb-0 text-sm text-n-slate-11">
          {{ copy.total }}
          <strong
            class="text-base tabular-nums text-n-slate-12"
            data-test="quote-total"
          >
            {{ currency(total) }}
          </strong>
        </p>
        <div class="flex flex-col-reverse gap-2 sm:flex-row">
          <Button
            variant="faded"
            color="slate"
            :label="copy.cancel"
            :disabled="isSending"
            type="button"
            @click="close"
          />
          <Button
            variant="faded"
            color="slate"
            :label="editingQuote ? 'Salvar alterações' : 'Criar orçamento'"
            :disabled="!canSend || !validValidity"
            type="button"
            data-test="save-quote"
            @click="saveQuote"
          />
          <Button
            color="blue"
            icon="i-hugeicons-sent"
            :label="isSending ? copy.sending : copy.send"
            :disabled="!canSend || !validValidity"
            :is-loading="isSending"
            type="button"
            data-test="review-quote"
            @click="reviewAndSend"
          />
        </div>
      </div>
    </template>
  </Dialog>
  <ContractSendDialog
    ref="sendDialogRef"
    :is-sending="isSending"
    :quote-templates="quoteTemplates"
    @send-quote="handleSendQuote"
    @generate-quote-pdf="handleGenerateQuotePdf"
    @generate-receipt="handleGenerateReceipt"
  />
</template>
